import { Alert, Box, Stack } from '@mui/material';
import { sub } from 'date-fns';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD, CELLULAR_SIGNAL_WEAK_THRESHOLD } from '@/api/devices/useDevice';
import {
    DeviceCellularSignalHistoryItem,
    useDeviceCellularSignalHistory,
} from '@/api/historicalData/useDeviceCellularSignalHistory';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { AlertLine } from '@/charts/types';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { ChartDataDetails, ChartWithInfo } from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineCellularSignalConsistency,
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { Timeframe, TimeframeButtonGroup } from '@/components/spotter/notificationTab/TimeframeButtonGroup';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FixedDateRangeContextProvider, useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useTzContext } from '@/hooks/useTzContext';

type LineConfig = Line<DeviceCellularSignalHistoryItem>;

export const CELLULAR_SIGNAL_STRENGTH_LINE = {
    id: 'cellular-signal-strength',
    label: 'Cellular signal strength',
    valueKey: 'rsrp' as const,
    color: colorsets.blue,
    unit: 'dBm',
    unitType: 'signalStrength',
} satisfies LineConfig;

const LINES = [CELLULAR_SIGNAL_STRENGTH_LINE];

export type CellularSignalChartProps = {
    chartTitle: string;
    spotterId: string;
    alertLines?: AlertLine[];
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<DeviceCellularSignalHistoryItem> | null) => void;
};

export const CellularSignalStrengthChart = ({
    chartTitle,
    spotterId,
    alertLines,
    lines,
    onSelectData,
}: CellularSignalChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<DeviceCellularSignalHistoryItem> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null);
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `cellular-signal-strength-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    useTrackChartView({
        containerEl,
        spotterId: spotterId!,
        chartTitle,
    });

    const { data: deviceCellularSignalHistory, isPending } = useDeviceCellularSignalHistory({
        spotterId: spotterId!,
        dateRange,
    });
    const drawChart = useCallback(drawLineChart, []);

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!deviceCellularSignalHistory) {
            return;
        }

        const newChart = drawChart<DeviceCellularSignalHistoryItem>({
            dataset: deviceCellularSignalHistory,
            lines,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Cellular signal strength',
            containerId,
            showAlertLines: true,
            scaleChartToFitAllAlerts: false,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp: setSelectedTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        lines,
        onSelectData,
        deviceCellularSignalHistory,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [deviceCellularSignalHistory?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const CellularSignalSection = () => {
    const chartTitle = 'Cellular signal history';
    const { spotterId } = useSpotterContext();
    const tz = useTzContext();

    const availableTimeframes: Timeframe[] = useMemo(
        () => [
            [3, 'days'],
            [7, 'days'],
            [1, 'months'],
            [6, 'months'],
        ],
        []
    );
    const [selectedData, setSelectedData] = useState<LineDataSlice<DeviceCellularSignalHistoryItem> | null>(null);
    const [timeframe, setTimeframe] = useState<Timeframe>([7, 'days']);
    const endDate = useMemo(() => new Date(), []);
    const startDate = useMemo(() => {
        const [value, units] = timeframe;
        return sub(endDate, { [units]: value }, tz);
    }, [endDate, timeframe, tz]);
    const dateRange = { startDate, endDate };
    const alertLines = useMemo(
        () => [
            {
                value: CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD,
                operator: '<' as const,
                label: 'Poor signal',
                severity: 'error' as const,
            },
            {
                value: CELLULAR_SIGNAL_WEAK_THRESHOLD,
                operator: '<' as const,
                label: 'Weak signal',
                severity: 'warning' as const,
            },
        ],
        []
    );

    const { data: deviceCellularSignalHistory, isPending } = useDeviceCellularSignalHistory({
        spotterId: spotterId!,
        dateRange,
    });

    return (
        <Stack gap={2} p={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionHeader title={chartTitle} variant="h5" />
                <Box sx={{ ml: 3 }}>
                    <TimeframeButtonGroup
                        currentTimeframe={timeframe}
                        availableTimeframes={availableTimeframes}
                        onTimeframeChange={setTimeframe}
                    />
                </Box>
            </Stack>
            <FixedDateRangeContextProvider startDate={startDate} endDate={endDate}>
                {isPending ? (
                    <GenericSkeleton />
                ) : deviceCellularSignalHistory?.length ? (
                    <Box>
                        <ChartWithInfo
                            chart={
                                <CellularSignalStrengthChart
                                    lines={LINES}
                                    spotterId={spotterId!}
                                    chartTitle={chartTitle}
                                    onSelectData={setSelectedData}
                                    alertLines={alertLines}
                                />
                            }
                        >
                            <ChartDataDetails>
                                {!selectedData && (
                                    <LineChartDataSummary
                                        dateRange={dateRange}
                                        lines={LINES}
                                        dataset={deviceCellularSignalHistory}
                                        renderLineSummary={() => (
                                            <>
                                                <DataLineMean />
                                                <DataLineExtent />
                                                <DataLineCellularSignalConsistency />
                                            </>
                                        )}
                                    />
                                )}
                                <DataDetailsByTimestamp
                                    selectedData={selectedData}
                                    datumRenderer={({ lines, datum }) => (
                                        <LineChartDatumDetail
                                            dataset={deviceCellularSignalHistory}
                                            datum={datum}
                                            lines={lines}
                                        />
                                    )}
                                />
                            </ChartDataDetails>
                        </ChartWithInfo>
                    </Box>
                ) : (
                    <Alert severity="info">No cellular signal strength data for selected date range</Alert>
                )}
            </FixedDateRangeContextProvider>
        </Stack>
    );
};
